import { useTranslation } from '@klaviyo-mkt/gatsby-components/hooks/useTranslation';

import InternationalizationProvider from '@klaviyo-mkt/gatsby-components/context/internationalization';
import { convertLocaleToLangCode } from '@klaviyo-mkt/gatsby-theme-core/src/gatsby/wrap-page-element';

import OpenGraph from '@klaviyo-mkt/gatsby-theme-marketing/src/templates/layout/head/children/open-graph';
import PageInfo from '@klaviyo-mkt/gatsby-theme-marketing/src/templates/layout/head/children/page-info';

import { formatUrl } from '@klaviyo-mkt/gatsby-theme-marketing/src/templates/layout/head/utils';

import type { IGlobalHead } from '@klaviyo-mkt/gatsby-theme-marketing/src/templates/layout/head/types';

const baseUrl = 'https://help.klaviyo.com';
const xDefaults = [
  'en-us',
];

export const StaticGlobalHead = ({
  children,
  langCode,
  name,
  pageType,
  pathname,
  seo,
  translations = [],
}: IGlobalHead & { langCode?: string; }) => {
  const { t } = useTranslation();

  const defaultImage = {
    publicUrl: `${baseUrl}/opengraph-image-${t('langCodes_localeLowerCase')}.png`,
  };

  let {
    title = t('helpCenter_globalHead_title'),
  } = seo || {};

  const {
    metaDesc = t('helpCenter_globalHead_metaDesc'),
    opengraphImage = defaultImage,
    opengraphSiteName = t('helpCenter_globalHead_opengraphSiteName'),
    twitterImage = defaultImage,
  } = seo || {};
  
  const pageTypes: { [key: string]: string; } = {
    article: t('helpCenter_globalHead_article'),
    category: t('helpCenter_globalHead_category'),
    notFound: t('pages_notFound_eyebrow'),
    section: t('helpCenter_globalHead_section'),
  };
  
  const generalPageType = pageType && pageTypes[pageType] ? `${pageTypes[pageType]} | ${t('helpCenter_globalHead_title')}` : t('helpCenter_globalHead_title');
  const fullName = name ? `${name} | ${t('helpCenter_globalHead_title')}` : generalPageType;
  const canonical = formatUrl(seo?.canonical || pathname, baseUrl);
  const ogImageUrl = opengraphImage.publicUrl ? formatUrl(opengraphImage.publicUrl, baseUrl) : undefined;
  const twitterImageUrl = twitterImage?.publicUrl ? formatUrl(twitterImage?.publicUrl, baseUrl) : ogImageUrl;
  title = fullName;

  const xDefault = translations.find(({ hreflang }) => xDefaults.includes(hreflang));

  return (
    <>
      {langCode && <html lang={langCode} />}
      <PageInfo
        description={metaDesc}
        title={title}
      />
      {Boolean(canonical) && <link id="link-rel-canonical" rel="canonical" href={canonical.replace(/\/$/, '')} />}
      <OpenGraph
        description={metaDesc}
        image={ogImageUrl}
        modifiedTime={seo?.opengraphModifiedTime}
        publisher={seo?.opengraphPublisher}
        siteName={opengraphSiteName}
        title={title}
        twitterDescription={metaDesc}
        twitterImage={twitterImageUrl}
        twitterTitle={title}
        url={canonical}
      />
      {translations.map(({ hreflang, url }) => (
        <link key={hreflang} rel="alternate" href={`${baseUrl}${url}`} hrefLang={hreflang} />
      ))}
      {xDefault && (
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${xDefault.url}`} />
      )}
      {children}
    </>
  );
};

export const GlobalHeadWithI18n = (props: IGlobalHead & { langCode?: string; }) => (
  <InternationalizationProvider langCode={convertLocaleToLangCode(props.langCode) ?? 'en-US'}>
    <StaticGlobalHead {...props} />
  </InternationalizationProvider>
);

export default GlobalHeadWithI18n;
